import React, { useState, useEffect } from 'react';
import { FiUsers, FiShoppingBag, FiDollarSign, FiTrendingUp, FiActivity, FiCalendar, FiSettings, FiAlertCircle, FiCheckCircle, FiClock, FiPackage, FiCreditCard, FiBarChart2, FiPieChart, FiZap, FiShield, FiGlobe, FiMail, FiPhone, FiMapPin, FiStar, FiAward, FiTarget, FiTrendingDown, FiFilter, FiRefreshCw, FiDownload, FiUpload, FiEdit, FiTrash2, FiEye, FiPlus, FiMinus, FiChevronRight, FiChevronDown, FiChevronUp, FiArrowUp, FiArrowDown, FiMoreVertical } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [dateRange, selectedPeriod]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/dashboard/stats', {
        params: { ...dateRange, period: selectedPeriod }
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color, trend, trendValue, badges = [] }) => (
    <div className="group relative overflow-hidden bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
      <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-14 h-14 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="text-white text-xl" />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {trend === 'up' ? <FiArrowUp /> : <FiArrowDown />}
              {trendValue}
            </div>
          )}
        </div>
        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">{value}</h3>
          <p className="text-gray-600 text-sm">{title}</p>
          {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
        </div>
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {badges.map((badge, index) => (
              <span key={index} className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
                {badge.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const QuickActionCard = ({ icon: Icon, title, description, color, onClick }) => (
    <button
      onClick={onClick}
      className="group relative overflow-hidden bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 p-6 text-left"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
      <div className="relative">
        <div className={`w-12 h-12 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="text-white text-xl" />
        </div>
        <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FiShield className="text-white text-xl" />
                </div>
                Super Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Monitor and manage your platform</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Period Selector */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {['today', 'week', 'month', 'year'].map((period) => (
                  <button
                    key={period}
                    onClick={() => setSelectedPeriod(period)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      selectedPeriod === period
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {period.charAt(0).toUpperCase() + period.slice(1)}
                  </button>
                ))}
              </div>

              {/* Date Range Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <FiCalendar className="text-gray-500" />
                <span className="text-sm font-medium">Custom Range</span>
                <FiChevronDown className={`text-gray-500 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>

              {/* Refresh Button */}
              <button
                onClick={fetchStats}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
              >
                <FiRefreshCw className="text-sm" />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>

          {/* Date Range Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">From:</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">To:</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FiUsers}
            title="Total Vendors"
            value={stats?.vendors?.total || 0}
            subtitle="Registered restaurants"
            color="from-blue-500 to-blue-600"
            trend="up"
            trendValue="12%"
            badges={[
              { label: `${stats?.vendors?.active || 0} Active`, color: 'bg-green-100 text-green-700' },
              { label: `${stats?.vendors?.pending || 0} Pending`, color: 'bg-amber-100 text-amber-700' }
            ]}
          />
          
          <StatCard
            icon={FiUsers}
            title="Total Customers"
            value={stats?.customers?.total || 0}
            subtitle="Active users"
            color="from-purple-500 to-purple-600"
            trend="up"
            trendValue="8%"
          />
          
          <StatCard
            icon={FiShoppingBag}
            title="Today's Orders"
            value={stats?.orders?.today || 0}
            subtitle="Orders placed today"
            color="from-orange-500 to-orange-600"
            trend="up"
            trendValue="15%"
            badges={[
              { label: `${stats?.orders?.completed || 0} Completed`, color: 'bg-green-100 text-green-700' }
            ]}
          />
          
          <StatCard
            icon={FiDollarSign}
            title="Total Revenue"
            value={`₹${(stats?.sales?.total || 0).toLocaleString()}`}
            subtitle="Lifetime revenue"
            color="from-green-500 to-green-600"
            trend="up"
            trendValue="23%"
          />
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            icon={FiPackage}
            title="Total Products"
            value={stats?.products?.total || 0}
            subtitle="Menu items across all vendors"
            color="from-pink-500 to-pink-600"
          />
          
          <StatCard
            icon={FiCreditCard}
            title="Transactions"
            value={stats?.transactions?.total || 0}
            subtitle="Payment transactions"
            color="from-indigo-500 to-indigo-600"
            trend="up"
            trendValue="18%"
          />
          
          <StatCard
            icon={FiActivity}
            title="Active Sessions"
            value={stats?.sessions?.active || 0}
            subtitle="Currently online"
            color="from-teal-500 to-teal-600"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FiActivity className="text-indigo-600" />
                    Recent Activity
                  </h3>
                  <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                    View All
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FiUsers className="text-indigo-600 text-sm" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">New vendor registration</p>
                        <p className="text-xs text-gray-600 mt-1">Restaurant "The Food Place" registered 2 minutes ago</p>
                        <p className="text-xs text-gray-500 mt-1">2 minutes ago</p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        New
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FiZap className="text-indigo-600" />
                Quick Actions
              </h3>
            </div>
            <div className="p-6 space-y-3">
              <QuickActionCard
                icon={FiUsers}
                title="Manage Vendors"
                description="View and approve vendor applications"
                color="from-blue-500 to-blue-600"
                onClick={() => console.log('Navigate to vendors')}
              />
              
              <QuickActionCard
                icon={FiCreditCard}
                title="Subscription Plans"
                description="Manage pricing and subscription tiers"
                color="from-green-500 to-green-600"
                onClick={() => console.log('Navigate to subscriptions')}
              />
              
              <QuickActionCard
                icon={FiSettings}
                title="System Settings"
                description="Configure platform settings"
                color="from-purple-500 to-purple-600"
                onClick={() => console.log('Navigate to settings')}
              />
              
              <QuickActionCard
                icon={FiBarChart2}
                title="Analytics"
                description="View detailed analytics and reports"
                color="from-orange-500 to-orange-600"
                onClick={() => console.log('Navigate to analytics')}
              />
            </div>
          </div>
        </div>

        {/* Bottom Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {/* Performance Metrics */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiTrendingUp className="text-indigo-600" />
              Performance Metrics
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <FiCheckCircle className="text-green-600 text-2xl mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">98.5%</p>
                <p className="text-sm text-green-600">Uptime</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <FiZap className="text-blue-600 text-2xl mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-700">1.2s</p>
                <p className="text-sm text-blue-600">Avg Response</p>
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiShield className="text-indigo-600" />
              System Health
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Database</span>
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Healthy</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">API Services</span>
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Operational</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Storage</span>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">65% Used</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
